import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import AudioPlayer from "@/components/AudioPlayer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";
import {
  Sparkles,
  Wand2,
  Download,
  Loader2,
  Music,
  Mic,
  Zap,
  Volume2,
  Star,
} from "lucide-react";

const MODULES = [
  { key: "adscript", label: "AdScript → Jingle", icon: Mic, desc: "Standard jingle generator" },
  { key: "reelsong", label: "ReelSong", icon: Zap, desc: "Reels & Shorts hook" },
  { key: "localads", label: "Local Ads", icon: Volume2, desc: "Hindi-first templates" },
  { key: "brandanthem", label: "Brand Anthem", icon: Star, desc: "30-60s storytelling" },
];

const LOCAL_TEMPLATES = {
  coaching: "Join Bright Futures Coaching — Top ranks since 2005. Hindi-English medium available. Admissions open for Class 9-12. Call 98765-43210 now.",
  real_estate: "Now launching Green Valley Homes — 2 & 3 BHK premium apartments starting ₹45 lakhs. Book today, move in 6 months. Site visit this Sunday.",
  retail: "Verma Electronics mein aaya hai Diwali Dhamaka! Har TV pe 30% tak discount. Free home delivery. Aaj hi visit kare, Shop No. 4 Main Market.",
  salon: "Glow Unisex Salon — Ab Navratri Special. Haircut + Facial combo sirf ₹999. Book online, 20% extra off on first visit. Open 10 AM to 9 PM.",
};

export default function Generate() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();

  const [mode, setMode] = useState("paste"); // "paste" | "ai"
  const [script, setScript] = useState("");
  const [generatingScript, setGeneratingScript] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [sampleLoading, setSampleLoading] = useState(false);
  const [sampleUrl, setSampleUrl] = useState(null);
  const sampleAudioRef = React.useRef(null);

  // AI script inputs
  const [product, setProduct] = useState("");
  const [audience, setAudience] = useState("");
  const [offer, setOffer] = useState("");
  const [scriptTone, setScriptTone] = useState("fun");

  // Generation config
  const [module, setModule] = useState("adscript");
  const [template, setTemplate] = useState("coaching");
  const [duration, setDuration] = useState("30");
  const [language, setLanguage] = useState("hinglish");
  const [voice, setVoice] = useState("male");
  const [genre, setGenre] = useState("pop");
  const [mood, setMood] = useState("energetic");
  const [genres, setGenres] = useState([]);
  const [moods, setMoods] = useState([]);

  // Reset sample preview when genre/mood/voice changes
  useEffect(() => { setSampleUrl(null); }, [genre, mood, voice]);

  useEffect(() => {
    apiClient.get("/music/options").then(({ data }) => {
      setGenres(data.genres || []);
      setMoods(data.moods || []);
    }).catch(() => {});
  }, []);

  const applyTemplate = (t) => {
    setTemplate(t);
    setScript(LOCAL_TEMPLATES[t]);
  };

  const handleSample = async () => {
    setSampleLoading(true);
    try {
      const { data } = await apiClient.get("/music/sample", {
        params: { genre, mood, voice },
      });
      setSampleUrl(data.audio_url);
      // Auto-play once loaded
      setTimeout(() => {
        if (sampleAudioRef.current) {
          sampleAudioRef.current.currentTime = 0;
          sampleAudioRef.current.play().catch(() => {});
        }
      }, 100);
      if (!data.cached) toast.success("Fresh sample generated!");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Sample failed to load");
    } finally {
      setSampleLoading(false);
    }
  };

  const handleAIGenerate = async () => {
    if (!product.trim()) {
      toast.error("Please describe your product or service.");
      return;
    }
    setGeneratingScript(true);
    try {
      const { data } = await apiClient.post("/scripts/generate", {
        product,
        audience,
        offer,
        tone: scriptTone,
        language,
      });
      setScript(data.script);
      toast.success("Script ready! Edit as needed, then generate your jingle.");
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Script generation failed");
    } finally {
      setGeneratingScript(false);
    }
  };

  const handleGenerateJingle = async () => {
    if (!script.trim()) {
      toast.error("Please paste or generate a script first.");
      return;
    }
    const durSec = parseInt(duration, 10);
    const isFree = (user?.subscription_tier || "free") === "free";
    if (isFree && durSec > 15) {
      toast.error("Free trial supports only 15-second jingles. Upgrade to unlock longer durations.");
      return;
    }
    if (isFree && !["adscript", "localads"].includes(module)) {
      toast.error("Free trial supports only AdScript and Local Ads modules. Upgrade to unlock ReelSong and Brand Anthem.");
      return;
    }
    if ((user?.credits ?? 0) < durSec) {
      toast.error(`This jingle needs ${durSec} credits, you have ${user?.credits ?? 0}. Upgrade to continue.`);
      navigate("/pricing");
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      const { data } = await apiClient.post("/jingles/generate", {
        script,
        module,
        duration_sec: parseInt(duration, 10),
        language,
        voice,
        genre,
        mood,
        template: module === "localads" ? template : null,
      });
      setResult(data);
      toast.success("Your jingle is ready!");
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Jingle generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const download = async (fmt = "mp3") => {
    if (!result?.jingle_id) return;
    const res = await apiClient.get(`/jingles/${result.jingle_id}/audio`, {
      params: { format: fmt },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `jingle_${result.jingle_id}.${fmt}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="generate-page">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="mb-10">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 mb-2">
            New jingle
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter">
            Compose your jingle.
          </h1>
          <p className="text-zinc-600 mt-3">
            Pick a module, write or AI-generate a script, hit generate.
          </p>
        </div>

        {/* Module selector */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">
            01 · Module
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {MODULES.map((m) => {
              const Icon = m.icon;
              const active = module === m.key;
              const isFree = (user?.subscription_tier || "free") === "free";
              const locked = isFree && !["adscript", "localads"].includes(m.key);
              return (
                <button
                  key={m.key}
                  onClick={() => !locked && setModule(m.key)}
                  disabled={locked}
                  className={`relative text-left p-4 border transition-all ${
                    active
                      ? "bg-black text-white border-black"
                      : locked
                      ? "bg-zinc-50 border-black/10 opacity-60 cursor-not-allowed"
                      : "bg-white border-black/10 hover:border-black"
                  }`}
                  data-testid={`module-select-${m.key}`}
                >
                  {locked && (
                    <div className="absolute top-2 right-2 text-[9px] uppercase tracking-widest font-bold text-[#FF4500] bg-white border border-[#FF4500] px-1.5 py-0.5">
                      PRO
                    </div>
                  )}
                  <Icon size={18} className={active ? "text-[#FF4500]" : ""} />
                  <div className="font-display text-base font-semibold mt-3">
                    {m.label}
                  </div>
                  <div className={`text-xs mt-1 ${active ? "text-white/60" : "text-zinc-500"}`}>
                    {m.desc}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-6">
          {/* LEFT — script */}
          <section className="md:col-span-7">
            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">
              02 · Script
            </div>
            <Tabs value={mode} onValueChange={setMode} className="w-full">
              <TabsList className="grid grid-cols-2 w-full rounded-none bg-[#F8F9FA] p-1 h-auto">
                <TabsTrigger
                  value="paste"
                  className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white py-2"
                  data-testid="tab-paste-script"
                >
                  Paste script
                </TabsTrigger>
                <TabsTrigger
                  value="ai"
                  className="rounded-none data-[state=active]:bg-black data-[state=active]:text-white py-2"
                  data-testid="tab-ai-script"
                >
                  AI generate
                </TabsTrigger>
              </TabsList>

              <TabsContent value="paste" className="mt-4">
                {module === "localads" && (
                  <div className="mb-3">
                    <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-2">
                      Quick template
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(LOCAL_TEMPLATES).map((t) => (
                        <button
                          key={t}
                          onClick={() => applyTemplate(t)}
                          className={`text-xs px-3 py-2 border ${
                            template === t
                              ? "bg-[#FF4500] text-white border-[#FF4500]"
                              : "bg-white border-black/20 hover:border-black"
                          }`}
                          data-testid={`template-${t}`}
                        >
                          {t.replace("_", " ")}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <Textarea
                  value={script}
                  onChange={(e) => setScript(e.target.value)}
                  placeholder="Paste your ad script here... Keep it catchy, rhythmic, and under 80 words for best results."
                  className="min-h-[240px] rounded-sm border-black/20 focus-visible:ring-[#FF4500]"
                  maxLength={4000}
                  data-testid="script-textarea"
                />
                <div className="mt-2 font-mono text-xs text-zinc-500">
                  {script.length} characters · {script.trim().split(/\s+/).filter(Boolean).length} words
                </div>
              </TabsContent>

              <TabsContent value="ai" className="mt-4 space-y-4">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Product / service *</Label>
                    <Input
                      value={product}
                      onChange={(e) => setProduct(e.target.value)}
                      placeholder="e.g. Premium organic honey"
                      className="mt-1 rounded-sm border-black/20"
                      data-testid="ai-product-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Target audience</Label>
                    <Input
                      value={audience}
                      onChange={(e) => setAudience(e.target.value)}
                      placeholder="e.g. Urban moms 25-40"
                      className="mt-1 rounded-sm border-black/20"
                      data-testid="ai-audience-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Offer / USP</Label>
                    <Input
                      value={offer}
                      onChange={(e) => setOffer(e.target.value)}
                      placeholder="e.g. 20% off, free delivery"
                      className="mt-1 rounded-sm border-black/20"
                      data-testid="ai-offer-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Tone</Label>
                    <Select value={scriptTone} onValueChange={setScriptTone}>
                      <SelectTrigger className="mt-1 rounded-sm border-black/20" data-testid="ai-tone-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fun">Funny</SelectItem>
                        <SelectItem value="emotional">Emotional</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  onClick={handleAIGenerate}
                  disabled={generatingScript}
                  className="bg-black hover:bg-zinc-800 text-white rounded-sm h-10 px-5"
                  data-testid="ai-generate-script-btn"
                >
                  {generatingScript ? (
                    <><Loader2 size={16} className="mr-2 animate-spin" /> Writing…</>
                  ) : (
                    <><Wand2 size={16} className="mr-2" /> Generate script with AI</>
                  )}
                </Button>

                {script && (
                  <div>
                    <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Generated script (editable)</Label>
                    <Textarea
                      value={script}
                      onChange={(e) => setScript(e.target.value)}
                      className="mt-1 min-h-[180px] rounded-sm border-black/20 focus-visible:ring-[#FF4500]"
                      data-testid="ai-script-textarea"
                    />
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </section>

          {/* RIGHT — config */}
          <aside className="md:col-span-5">
            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">
              03 · Configuration
            </div>
            <div className="bg-[#F8F9FA] border border-black/10 p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Duration</Label>
                  <Select value={duration} onValueChange={setDuration}>
                    <SelectTrigger className="mt-1 rounded-sm bg-white border-black/20" data-testid="duration-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 sec {(user?.subscription_tier || "free") === "free" && "(free)"}</SelectItem>
                      <SelectItem value="20" disabled={(user?.subscription_tier || "free") === "free"}>20 sec {(user?.subscription_tier || "free") === "free" && "· PRO"}</SelectItem>
                      <SelectItem value="30" disabled={(user?.subscription_tier || "free") === "free"}>30 sec {(user?.subscription_tier || "free") === "free" && "· PRO"}</SelectItem>
                      {module === "brandanthem" && <SelectItem value="60" disabled={(user?.subscription_tier || "free") === "free"}>60 sec · PRO</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Language</Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger className="mt-1 rounded-sm bg-white border-black/20" data-testid="language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="hinglish" data-testid="lang-option-hinglish">Hinglish</SelectItem>
                      <SelectItem value="hindi" data-testid="lang-option-hindi">Hindi · हिंदी</SelectItem>
                      <SelectItem value="english" data-testid="lang-option-english">English</SelectItem>
                      <SelectItem value="tamil" data-testid="lang-option-tamil">Tamil · தமிழ்</SelectItem>
                      <SelectItem value="telugu" data-testid="lang-option-telugu">Telugu · తెలుగు</SelectItem>
                      <SelectItem value="bengali" data-testid="lang-option-bengali">Bengali · বাংলা</SelectItem>
                      <SelectItem value="marathi" data-testid="lang-option-marathi">Marathi · मराठी</SelectItem>
                      <SelectItem value="gujarati" data-testid="lang-option-gujarati">Gujarati · ગુજરાતી</SelectItem>
                      <SelectItem value="punjabi" data-testid="lang-option-punjabi">Punjabi · ਪੰਜਾਬੀ</SelectItem>
                      <SelectItem value="kannada" data-testid="lang-option-kannada">Kannada · ಕನ್ನಡ</SelectItem>
                      <SelectItem value="malayalam" data-testid="lang-option-malayalam">Malayalam · മലയാളം</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Voice</Label>
                  <Select value={voice} onValueChange={setVoice}>
                    <SelectTrigger className="mt-1 rounded-sm bg-white border-black/20" data-testid="voice-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Mood</Label>
                  <Select value={mood} onValueChange={setMood}>
                    <SelectTrigger className="mt-1 rounded-sm bg-white border-black/20" data-testid="mood-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      {moods.length === 0 ? (
                        <SelectItem value="energetic">Energetic</SelectItem>
                      ) : (
                        moods.map((m) => (
                          <SelectItem key={m.key} value={m.key} data-testid={`mood-option-${m.key}`}>
                            {m.label}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest font-bold text-zinc-500">Genre</Label>
                <Select value={genre} onValueChange={setGenre}>
                  <SelectTrigger className="mt-1 rounded-sm bg-white border-black/20" data-testid="genre-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {genres.length === 0 ? (
                      <SelectItem value="pop">Pop</SelectItem>
                    ) : (
                      genres.map((g) => (
                        <SelectItem key={g.key} value={g.key} data-testid={`genre-option-${g.key}`}>
                          {g.label}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Listen to Sample */}
              <div className="pt-1">
                <button
                  type="button"
                  onClick={handleSample}
                  disabled={sampleLoading}
                  className="w-full flex items-center justify-center gap-2 h-10 px-4 border border-black/20 bg-white hover:bg-black hover:text-white transition-colors text-sm font-semibold disabled:opacity-50 disabled:cursor-wait"
                  data-testid="sample-listen-btn"
                >
                  {sampleLoading ? (
                    <><Loader2 size={14} className="animate-spin" /> Composing sample…</>
                  ) : (
                    <><Volume2 size={14} /> Listen to {(genres.find(g => g.key === genre) || {}).label || "sample"} · {mood} · {voice} preview</>
                  )}
                </button>
                {sampleUrl && (
                  <div className="mt-3 p-3 bg-black text-white flex items-center gap-3" data-testid="sample-player">
                    <Music size={16} className="text-[#FF4500] shrink-0" />
                    <div className="text-[10px] uppercase tracking-widest font-bold shrink-0">10s preview</div>
                    <audio
                      ref={sampleAudioRef}
                      src={sampleUrl}
                      controls
                      className="flex-1 h-8"
                      style={{ filter: "invert(1) hue-rotate(180deg)" }}
                    />
                  </div>
                )}
                <div className="mt-2 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
                  Free · cached after first play
                </div>
              </div>

              <div className="h-px bg-black/10 my-4" />
              {(user?.subscription_tier || "free") === "free" && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 text-amber-900 text-xs" data-testid="free-tier-notice">
                  <strong>Free trial:</strong> 15s max · AdScript & Local Ads only. <a href="/pricing" className="underline font-bold">Upgrade</a> for all durations, modules & 60s anthems.
                </div>
              )}
              <Button
                onClick={handleGenerateJingle}
                disabled={generating || !script.trim() || (user?.credits ?? 0) < parseInt(duration, 10)}
                className="w-full bg-[#FF4500] hover:bg-[#E03C00] text-white rounded-sm h-12 text-base font-semibold disabled:opacity-50"
                data-testid="generate-jingle-btn"
              >
                {generating ? (
                  <><Loader2 size={18} className="mr-2 animate-spin" /> Generating jingle… (45–90s)</>
                ) : (
                  <><Sparkles size={18} className="mr-2" /> Generate jingle · {duration} credits</>
                )}
              </Button>
              <div className="text-center font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                {user?.credits ?? 0} credits remaining ({Math.floor((user?.credits ?? 0) / 30)} × 30s jingles)
              </div>
            </div>
          </aside>
        </div>

        {/* RESULT */}
        {generating && (
          <div className="mt-10 p-8 border border-black/10 bg-[#F8F9FA] text-center fade-up" data-testid="generating-state">
            <div className="flex items-center justify-center gap-[3px] h-12 mb-5">
              {Array.from({ length: 32 }).map((_, i) => (
                <div
                  key={i}
                  className="w-1 bg-[#FF4500] wave-bar"
                  style={{
                    height: `${30 + ((i * 17) % 70)}%`,
                    animationDelay: `${i * 0.05}s`,
                  }}
                />
              ))}
            </div>
            <div className="font-display text-2xl font-semibold mb-2">
              Composing your jingle…
            </div>
            <div className="text-zinc-500 text-sm">
              Our AI is composing vocals, melody and instrumentation. This typically takes 45–90 seconds.
            </div>
          </div>
        )}

        {result && !generating && (
          <div className="mt-10 fade-up" data-testid="jingle-result">
            <div className="text-xs uppercase tracking-widest font-bold text-[#FF4500] mb-3">
              ✦ Your jingle is ready
            </div>
            {result.generation_mode !== "music" && (
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 text-amber-900 text-sm flex items-start gap-3" data-testid="fallback-notice">
                <div className="font-mono text-xs uppercase tracking-widest font-bold shrink-0 pt-0.5">Note</div>
                <div>
                  Generated as a <strong>high-quality voice-over</strong> because full music composition is temporarily unavailable. Please try again in a moment to get the full song.
                </div>
              </div>
            )}
            <div className="bg-white border border-black/10 p-6 md:p-8">
              <div className="flex items-start justify-between mb-5 gap-4 flex-wrap">
                <div>
                  <div className="font-display text-2xl md:text-3xl font-semibold tracking-tight">
                    {result.title}
                  </div>
                  <div className="flex items-center gap-3 mt-2 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                    <span className="px-2 py-1 bg-black text-white">{result.module}</span>
                    <span>{result.duration_sec}s</span>
                    <span>·</span>
                    <span>{result.language}</span>
                    <span>·</span>
                    <span>{result.voice}</span>
                    <span>·</span>
                    <span>{(result.genre || "").replace("_", " ")}</span>
                    <span>·</span>
                    <span>{result.mood}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => download("mp3")}
                    className="bg-black hover:bg-zinc-800 text-white rounded-sm h-10 px-4"
                    data-testid="result-download-mp3"
                  >
                    <Download size={16} className="mr-2" /> MP3
                  </Button>
                  <Button
                    onClick={() => download("wav")}
                    variant="outline"
                    className="rounded-sm h-10 px-4 border-black/20"
                    data-testid="result-download-wav"
                  >
                    <Download size={16} className="mr-2" /> WAV
                  </Button>
                </div>
              </div>

              <AudioPlayer audioUrl={result.audio_url} onDownload={() => download("mp3")} />

              <div className="mt-6 grid md:grid-cols-2 gap-5">
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">
                    Hook line
                  </div>
                  <div className="p-4 bg-[#FF4500] text-white font-display text-xl leading-snug" data-testid="result-hook-line">
                    "{result.hook_line}"
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">
                    Full lyrics
                  </div>
                  <pre className="whitespace-pre-wrap font-display text-base leading-relaxed bg-[#F8F9FA] border border-black/10 p-4 max-h-48 overflow-auto" data-testid="result-lyrics">
{result.lyrics}
                  </pre>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <Button
                  onClick={handleGenerateJingle}
                  variant="outline"
                  className="rounded-sm h-10 px-4 border-black/20"
                  disabled={generating}
                  data-testid="regenerate-btn"
                >
                  <Music size={16} className="mr-2" /> Regenerate variation
                </Button>
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-[#09090B] hover:bg-zinc-800 text-white rounded-sm h-10 px-4"
                  data-testid="go-dashboard-btn"
                >
                  Back to dashboard
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
